import { useState } from "react";
import { Flame, Landmark, HandCoins, MessageCircle, CheckCircle2, BedDouble } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "@/components/Reveal";
import { api, waLink } from "@/api";
import { useLang, tr } from "@/i18n";

const features = [
    {
        icon: Flame,
        title: "Traditional Gayawal Pandas",
        text: "Rituals performed by certified Gayawal Pandas following authentic vedic tradition.",
    },
    {
        icon: Landmark,
        title: "All Sacred Vedis",
        text: "Falgu River, Vishnupad Temple, Akshayavat, Pretshila and other applicable sacred vedis.",
    },
    {
        icon: HandCoins,
        title: "Transparent Pricing",
        text: "40% advance to confirm your booking, 60% payable on arrival. No hidden charges.",
    },
];

const ACC_IMG_1 =
    "https://images.unsplash.com/photo-1789121274510-8cafb2787337?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const ACC_IMG_2 = "/img/ritual-hall.jpg";

const assistance = [
    "AC / Non-AC hotel & dharamshala booking in Gaya and Bodh Gaya",
    "Pure satvik meals arranged for the family during rituals",
    "Station & airport pick-up and local family transit",
    "Arrangements based on your duration, budget and availability",
];

const inputCls =
    "w-full px-4 py-3 rounded-xl bg-white border border-[#E6DBCF] text-[#241D19] text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A17]/40 focus:border-[#C85A17] transition";

const StayForm = ({ whatsapp, t }) => {
    const [form, setForm] = useState({
        name: "", phone: "", email: "", checkin: "", checkout: "",
        members: "", stay_type: "Any preference", notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/stay-requests", {
                ...form,
                members: form.members ? Number(form.members) : null,
                checkin: form.checkin || null,
                checkout: form.checkout || null,
                email: form.email || null,
            });
            setDone(data);
            toast.success(`Stay request received — Reference #${data.id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not submit request. Please try WhatsApp.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div data-testid="stay-success-panel" className="bg-white rounded-2xl border border-[#E5C158] p-8 text-center shadow-lg">
                <CheckCircle2 size={40} className="mx-auto text-green-600" />
                <h3 className="mt-3 font-display text-2xl text-[#241D19]">{t("Stay Request Received")}</h3>
                <p className="mt-2 text-sm text-[#6E6259]">
                    {t("Reference")} <b className="text-[#241D19]" data-testid="stay-reference-id">#{done.id}</b> · {done.name}
                </p>
                <p className="mt-3 text-sm text-[#6E6259]">{t("Our team will contact you on WhatsApp with stay options & charges.")}</p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                        href={waLink(whatsapp, `Namaste, I submitted stay request #${done.id} (${done.checkin || "flexible"} to ${done.checkout || "flexible"}, ${done.members || ""} guests, ${done.stay_type}).`)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="stay-whatsapp-button"
                        className="px-7 py-3 rounded-full bg-[#C85A17] text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-[#9C370B] transition-colors"
                    >
                        <MessageCircle size={16} /> WhatsApp
                    </a>
                    <button
                        onClick={() => setDone(null)}
                        data-testid="stay-new-request-button"
                        className="px-7 py-3 rounded-full border border-[#E6DBCF] text-[#241D19] text-sm font-medium hover:bg-[#F4EDE2] transition-colors"
                    >
                        {t("New Request")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            data-testid="stay-request-form"
            className="bg-white rounded-2xl border border-[#E6DBCF] p-6 md:p-8 grid grid-cols-1 sm:grid-cols-2 gap-5 shadow-[0_2px_24px_rgba(36,29,25,0.06)]"
        >
            <div>
                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Full Name *")}</label>
                <input data-testid="stay-form-name-input" className={inputCls} required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("Your full name")} />
            </div>
            <div>
                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Phone / WhatsApp *")}</label>
                <input data-testid="stay-form-phone-input" className={inputCls} required type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 ..." />
            </div>
            <div>
                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Check-in Date")}</label>
                <input data-testid="stay-form-checkin-input" className={inputCls} type="date" value={form.checkin} onChange={(e) => set("checkin", e.target.value)} />
            </div>
            <div>
                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Check-out Date")}</label>
                <input data-testid="stay-form-checkout-input" className={inputCls} type="date" value={form.checkout} onChange={(e) => set("checkout", e.target.value)} />
            </div>
            <div>
                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Guests")}</label>
                <input data-testid="stay-form-members-input" className={inputCls} type="number" min="1" value={form.members} onChange={(e) => set("members", e.target.value)} placeholder={t("e.g. 4")} />
            </div>
            <div>
                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Stay Preference")}</label>
                <select
                    data-testid="stay-form-type-select"
                    className={inputCls}
                    value={form.stay_type}
                    onChange={(e) => set("stay_type", e.target.value)}
                >
                    <option value="Any preference">{t("Any preference")}</option>
                    <option value="Hotel (AC)">{t("Hotel (AC)")}</option>
                    <option value="Hotel (Non-AC)">{t("Hotel (Non-AC)")}</option>
                    <option value="Dharamshala">{t("Dharamshala")}</option>
                </select>
            </div>
            <div className="sm:col-span-2">
                <label className="text-xs font-medium text-[#6E6259] mb-1.5 block">{t("Additional Requirements")}</label>
                <textarea data-testid="stay-form-notes-input" className={`${inputCls} min-h-[80px]`} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder={t("Requirements (food, pickup, budget...)")} />
            </div>
            <button
                data-testid="stay-form-submit-button"
                disabled={submitting}
                className="sm:col-span-2 py-4 rounded-full bg-[#241D19] text-white font-semibold hover:bg-[#C85A17] transition-colors disabled:opacity-60"
            >
                {submitting ? t("Submitting...") : t("Submit Request")}
            </button>
        </form>
    );
};

export const Accommodation = ({ whatsapp }) => {
    const { lang } = useLang();
    const t = (s) => tr(lang, s);
    return (
    <>
        <section data-testid="why-us-section" className="py-16 bg-[#F4EDE2] border-y border-[#E6DBCF]">
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-10">
                {features.map((f, i) => (
                    <Reveal key={f.title} delay={i * 0.1} className="flex gap-4">
                        <div className="shrink-0 w-12 h-12 rounded-full bg-white border border-[#E5C158] flex items-center justify-center">
                            <f.icon size={20} className="text-[#C85A17]" />
                        </div>
                        <div>
                            <h3 className="font-serif-alt font-semibold text-xl text-[#241D19]">{t(f.title)}</h3>
                            <p className="mt-1.5 text-sm text-[#6E6259] leading-relaxed">{t(f.text)}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </section>

        <section id="accommodation" data-testid="accommodation-section" className="py-20 lg:py-28 bg-[#FAF6F0]">
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <Reveal className="lg:col-span-5 relative">
                    <div className="relative">
                        <img
                            src={ACC_IMG_1}
                            alt="Guest accommodation in Gaya"
                            className="rounded-2xl w-full h-80 object-cover border border-[#E6DBCF]"
                        />
                        <img
                            src={ACC_IMG_2}
                            alt="Satvik food arrangements"
                            className="absolute -bottom-10 -right-4 sm:-right-10 w-44 sm:w-56 h-40 sm:h-48 object-cover rounded-2xl border-4 border-[#FAF6F0] shadow-xl"
                        />
                    </div>
                </Reveal>
                <div className="lg:col-span-7 lg:pl-8 mt-10 lg:mt-0">
                    <Reveal>
                        <Overline>{t("Chapter 02 — Optional Assistance")}</Overline>
                        <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl text-[#241D19] tracking-tight">
                            {t("Accommodation & Fooding")}
                        </h2>
                        <p className="mt-4 text-[#6E6259] leading-relaxed max-w-xl">
                            {t("Dharm Sarovar Private Limited assists with stay and food arrangements based on your requirements, duration and availability. Charged separately from ritual packages.")}
                        </p>
                    </Reveal>
                    <div className="mt-8 space-y-4">
                        {assistance.map((item, i) => (
                            <Reveal key={item} delay={0.1 + i * 0.08} className="flex items-start gap-3">
                                <span className="font-display text-[#C85A17] text-sm mt-0.5">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <p className="text-sm text-[#241D19] leading-relaxed">{t(item)}</p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 md:px-12 mt-20">
                <Reveal>
                    <div className="flex items-center gap-3 mb-2">
                        <BedDouble size={22} className="text-[#C85A17]" />
                        <h3 className="font-display text-xl sm:text-2xl text-[#241D19] tracking-tight">
                            {t("Request Accommodation Assistance")}
                        </h3>
                    </div>
                    <p className="text-sm text-[#6E6259] mb-8">
                        {t("Tell us your stay & food requirements and our team will contact you with options and charges.")}
                    </p>
                    <StayForm whatsapp={whatsapp} t={t} />
                    <a
                        href={waLink(whatsapp, "Namaste, I need accommodation & fooding assistance for my Gaya Pinddaan visit.")}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="accommodation-request-button"
                        className="mt-6 inline-flex items-center gap-2 text-sm text-[#C85A17] font-medium hover:text-[#9C370B] transition-colors"
                    >
                        <MessageCircle size={16} /> {t("Chat on WhatsApp instead")}
                    </a>
                </Reveal>
            </div>
        </section>
    </>
    );
};
